<template>
  <div>
    <EditCode ref="editCode" class="mb-3 ml-3" v-if="workcenterId" :workcenterId="workcenterId" />
    <!-- Add progress bar -->
    <v-progress-linear v-if="loading" :color="$getColor('vuetifyColor1')" class="linear-progress"
      indeterminate></v-progress-linear>
    <!-- Add content -->
    <v-data-table :color="$getColor('vuetifyColor1')" :headers="responsiveHeaders" :items="items" :items-per-page="10"
      class="elevation-1" calculate-widths :mobile-breakpoint="0" show-expand :footer-props="{
        showFirstLastPage: true,
        'items-per-page-text': 'Elementos por página',

      }">
      <!-- template v-slot for group header -->
      <template v-slot:[`item.category`]="{ item }">
        <!-- v-chip for category -->
        <v-chip :color="getCategoryOptions(item.category).color" text-color="white">
          {{ getCategoryOptions(item.category).text }}
        </v-chip>
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <span class="font-weight-bold">{{ item.name }}</span>
      </template>
      <template v-slot:[`item.extraHour`]="{ item }">
        <v-icon v-if="item.extraHour" :color="$getColor('vuetifyColor1')">numbers</v-icon>
      </template>
      <template v-slot:[`item.admin`]="{ item }">
        <v-icon v-if="item.admin" :color="$getColor('vuetifyColor2')">admin_panel_settings</v-icon>
      </template>
      <template v-slot:[`item.autoVariable`]="{ item }">
        <v-icon v-if="item.autoVariable" :color="$getColor('vuetifyColor2')">autorenew</v-icon>
      </template>
      <template v-slot:[`item.amount`]="{ item }">
        <span v-if="item.amount">{{ item.amount }} €</span>
      </template>
      <template v-slot:[`item.fixedMonthlyAmount`]="{ item }">
        <span v-if="item.fixedMonthlyAmount">{{ item.fixedMonthlyAmount }} €</span>
      </template>
      <template v-slot:[`item.weekend`]="{ item }">
        <v-btn-toggle
          v-if="item.category === 'variables'"
          v-model="item.weekend"
          mandatory
          dense
          class="weekend-toggle-group"
          @change="updateWeekend(item)"
        >
          <v-btn value="1" x-small :class="{ 'orange darken-2 white--text': item.weekend === '1' }">
            Festivo
          </v-btn>
          <v-btn value="0" x-small :class="{ 'primary white--text': item.weekend === '0' }">
            Laborable
          </v-btn>
          <v-btn value="all" x-small :class="{ 'grey darken-2 white--text': item.weekend === 'all' }">
            Cualquiera
          </v-btn>
        </v-btn-toggle>
        <span v-else class="caption text--secondary">-</span>
      </template>
      <template v-slot:[`item.monthly`]="{ item }">
        <span>{{ getTextFromMonthlyOptions(item.monthly) }}</span>
      </template>
      <template v-slot:[`item.type`]="{ item }">
        <span class="caption font-italic">{{ getTextFromTypeOptions(item.type) }}</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon depressed small :dark="$getPrimaryButton('dark')" :rounded="$getPrimaryButton('rounded')"
              :outlined="$getPrimaryButton('outlined')" :color="$getPrimaryButton('color')" @click="editCode(item)"
              v-bind="attrs" v-on="on">
              <v-icon>edit</v-icon>
            </v-btn>
          </template>
          <span>Editar código N2</span>
        </v-tooltip>
      </template>
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <v-list dense v-if="isMobile" class="mb-2">
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Nombre</v-list-item-subtitle>
                <v-list-item-title class="font-weight-bold">{{ item.name || '-' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Atributos</v-list-item-subtitle>
                <v-list-item-title>
                  <v-icon v-if="item.admin" small :color="$getColor('vuetifyColor2')">admin_panel_settings</v-icon>
                  <v-icon v-if="item.autoVariable" small :color="$getColor('vuetifyColor2')">autorenew</v-icon>
                  <v-icon v-if="item.extraHour" small :color="$getColor('vuetifyColor1')">numbers</v-icon>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Tipo</v-list-item-subtitle>
                <v-list-item-title>
                  <v-chip small :color="getCategoryOptions(item.category).color" text-color="white">
                    {{ getCategoryOptions(item.category).text }}
                  </v-chip>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Requisitos</v-list-item-subtitle>
                <v-list-item-title style="font-size: 0.85rem">
                  <span>{{ getTextFromMonthlyOptions(item.monthly) }}</span><span v-if="item.type">, </span>
                  <span>{{ getTextFromTypeOptions(item.type) }}</span><span v-if="item.weekend">, </span>
                  <span>{{ getTextFromWeekendOptions(item.weekend) }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="item.amount">
              <v-list-item-content>
                <v-list-item-subtitle>Importe</v-list-item-subtitle>
                <v-list-item-title>{{ item.amount }} €</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="item.fixedMonthlyAmount">
              <v-list-item-content>
                <v-list-item-subtitle>Importe mensual</v-list-item-subtitle>
                <v-list-item-title>{{ item.fixedMonthlyAmount }} €</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </td>
      </template>
    </v-data-table>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import EditCode from '@/views/_FATDA/components/EditCode.vue';
// eslint-disable-next-line import/no-cycle
import workcenterService from '@/services/workcenter.service';

export default {
  name: 'codes-view',
  props: {
    workcenterId: {
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loaded: false,
      dataCountLoaded: 0,
      adminAvailable: false,
      items: [],
      count: 0,
      headers: [
        { text: 'Codigo', value: 'code', groupable: false },
        { text: 'Admin', value: 'admin', width: '90px' },
        { text: 'Auto', value: 'autoVariable', width: '90px' },
        { text: 'Unidades', value: 'extraHour', width: '100px' },
        { text: 'Nombre', value: 'name', groupable: false },
        { text: 'Tipo', value: 'category' },
        { text: 'Importe', value: 'amount', groupable: false },
        { text: 'Importe mensual', value: 'fixedMonthlyAmount', groupable: false },
        { text: 'Periodo', value: 'monthly', groupable: false },
        {
          text: 'Festivo', value: 'weekend', groupable: false, width: '230px',
        },
        { text: 'Turno', value: 'type', groupable: false },
        { text: 'Acciones', value: 'actions' },
      ],
      categoryOptions: [
        { text: 'Variable', value: 'variables', color: 'primary' },
        { text: 'Absentismo', value: 'absences', color: 'success' },
      ],

      typeOptions: [
        { text: 'Turno de mañana/tarde', value: 'morning/evening' },
        { text: 'Turno de Noche', value: 'night' },
        { text: 'Todos', value: 'all' },
      ],

      weekendOptions: [
        { text: 'Festivo', value: '1' },
        { text: 'Laborable', value: '0' },
        { text: 'Cualquiera', value: 'all' },
      ],
      monthlyOptions: [
        { text: 'Mensual', value: true },
        { text: 'Quincenal', value: false },
      ],
      snackbar: {
        show: false,
        text: '',
        color: 'success',
      },
    };
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.width < 960;
    },
    responsiveHeaders() {
      if (this.isMobile) {
        return this.headers.filter((h) => h.value === 'code' || h.value === 'actions' || h.value === 'data-table-expand');
      }
      return this.headers;
    },
  },
  components: {
    EditCode,
  },
  mounted() {
    this.adminAvailable = this.$store.getters['auth/hasRole'](['ROLE_SUPER_ADMIN', 'ROLE_ADMIN']);
    if (!this.adminAvailable) {
      this.$router.push('/acceso-denegado');
    } else {
      this.tabMain = 0;
      this.getItems();
    }
  },
  methods: {
    addNew() {
      this.$router.push({ name: 'CreateSapCode' });
    },
    getItems() {
      this.loading = true;
      workcenterService.getN2Codes(this.workcenterId)
        .then((response) => {
          this.items = response;
          this.count = response.length;
          this.loaded = true;
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
          this.loading = false;
          this.loaded = false;
        });
    },
    updateWeekend(item) {
      workcenterService.editN2Code(this.workcenterId, item)
        .then(() => {
          this.showSnackbar('Festivo actualizado correctamente');
        })
        .catch((error) => {
          console.error('Error al actualizar tipo de festivo:', error);
          this.showSnackbar('Error al actualizar el registro', 'error');
          this.getItems();
        });
    },
    showSnackbar(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    getTextFromWeekendOptions(value) {
      const option = this.weekendOptions.find((item) => item.value === value);
      return option ? option.text : '';
    },
    getTextFromTypeOptions(value) {
      const option = this.typeOptions.find((item) => item.value === value);
      return option ? option.text : '';
    },
    getCategoryOptions(value) {
      const option = this.categoryOptions.find((item) => item.value === value);
      option.color = option.color || 'rgb(255, 64, 129)';
      option.text = option.text || ' - ';
      return option;
    },
    getTextFromMonthlyOptions(value) {
      const option = this.monthlyOptions.find((item) => item.value === value);
      return option ? option.text : '';
    },
    editCode(code) {
      this.$refs.editCode.updateFormData(code);
    },
  },
};
</script>
